import { h } from 'preact'
import { PureComponent } from 'preact-compat'
import { HomepageElement } from '../homepage'
import TextButton from '../../control-components/ui-components/TextButton'
import Icon, { IconType } from '../assets'
import { GenericHomepageElementTypes } from '../homepage/index'
import EmptyPlaceholder from './placeholder'
import WidgetConfigurator from './configurator'
import { ElementChange, DonePayload, MovePayload, VisiblePayload, pageOptions } from './toolbar'
import { isHtmlElement } from '../../libs/typeGuards'
import { bindPreactComponents } from '../../libs/preact-entry'
import Axios, { AxiosResponse, CancelTokenSource } from 'axios'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import ContainerEditor from './container-editor'

export interface HomepageEditorProps {
  parentEl: HTMLElement
  idx: number
  edit: boolean
  isMovable: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  homepageElement: HomepageElement
  totalCount: number
  onEdit: (el: HomepageElement) => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  onClose: () => void
  onMove: (idx: number, m: MovePayload) => void
  onRemove: (idx: number) => void
  onDone: (change: DonePayload) => void
  onVisibleToggle: (idx: number, v: VisiblePayload) => void
  disabled: boolean
  controlUrl: string
  currentPage: pageOptions
}

interface State {
  controlsVisible: boolean
  isConfigured: boolean
  configError: string
  loading: boolean
}

export const sharedWidgetsList = ['header_navigation', 'header', 'navigation_bar']

export const typeMap = {
  hero_banner: 'Hero Banner',
  html_widget: 'HTML Widget',
  quick_links: 'Quick Links',
  community_category: 'Community categories',
  knowledge_base: 'Knowledge Base',
  recommendations: 'Recommendations',
  featured_topics: 'Featured Topics',
  category_and_topics_stream: 'Community content and sidebar',
  category_and_topics_stream_only: 'Community content & category',
  container_2_1: 'Container 2:1',
  leaderboard_points: 'Leaderboard',
  event_calendar: 'Events calendar',
  badges: 'Badges',
  solved_topics: 'Solved Topics',
  most_liked: 'Most Liked',
  community_statistics: 'Community Statistics',
  [GenericHomepageElementTypes.header]: 'Header',
  [GenericHomepageElementTypes.footer]: 'Footer',
  [GenericHomepageElementTypes.navigation_bar]: 'Navigation',
  [GenericHomepageElementTypes.statistics_bar]: 'Statistics',
  [GenericHomepageElementTypes.introduction_bar]: 'Introduction',
  [GenericHomepageElementTypes.feature_topics]: 'Featured Topics (moderator tags)',
  [GenericHomepageElementTypes.announcement_card]: 'Announcement Card',
  [GenericHomepageElementTypes.breadcrumb]: 'Breadcrumb',
  [GenericHomepageElementTypes.ideation_statistics_bar]: 'Ideation Statistics',
  tag_cloud: 'Tag Cloud',
  groups: 'Groups',
  ideation_pipeline: 'Idea Pipeline',
  ideation_hero: 'Hero Banner',
  ideation_list: 'Idea List',
  ideation_status_filter: 'Idea Status Filter',
  product_area_filter: 'Product Area Filter',
  header_navigation: 'Mega Menu',
  'product-updates_hero': 'Hero Banner',
  'product-updates_list': 'Product Updates List',
  'product-updates_statistics_bar': 'Statistics',
  generic_hero: 'Hero Banner'
}

interface TabButtonProps {
  onClick: () => void
  icon: IconType
}

export const Shared = () => <span className='shared-widget'>Shared</span>

const TabButton = ({ onClick, icon }: TabButtonProps) => {
  return (
    <div class={`widget-tab__button widget-tab__button--${icon}`}>
      <TextButton onClick={onClick}>
        <Icon size={20} icon={icon} color='currentColor' />
      </TextButton>
    </div>
  )
}

function isEditableWidget (element: HomepageElement): boolean {
  return [
    'knowledge_base',
    'html_widget',
    'recommendations',
    'hero_banner',
    'generic_hero',
    'community_category',
    'category_and_topics_stream',
    'category_and_topics_stream_only',
    'leaderboard_points',
    'event_calendar',
    'featured_topics',
    GenericHomepageElementTypes.feature_topics,
    'recommendations',
    'quick_links',
    'badges',
    'tag_cloud',
    'solved_topics',
    'most_liked',
    'community_statistics',
    'groups',
    'ideation_pipeline',
    'ideation_hero',
    'product-updates_hero',
    'header_navigation',
    GenericHomepageElementTypes.announcement_card
  ].includes(element.type)
}

function isRemovableWidget (element: HomepageElement, page: pageOptions = 'homepage') {
  const list = [
    'html_widget',
    'tag_cloud',
    'community_category',
    'hero_banner',
    'badges',
    'solved_topics',
    'most_liked',
    'leaderboard_points',
    'event_calendar',
    'community_statistics',
    'recommendations',
    'quick_links',
    'featured_topics',
    'category_and_topics_stream',
    'category_and_topics_stream_only',
    'knowledge_base',
    'groups',
    'ideation_pipeline',
    'ideation_hero',
    'product-updates_hero',
    GenericHomepageElementTypes.feature_topics,
    GenericHomepageElementTypes.announcement_card,
    GenericHomepageElementTypes.introduction_bar,
    GenericHomepageElementTypes.statistics_bar,
    GenericHomepageElementTypes.ideation_statistics_bar
  ]

  if (page !== 'ideation') {
    list.push('container_2_1')
  }
  return list.includes(element.type)
}

export default class HomepageEditor extends PureComponent<HomepageEditorProps, State> {
  placeholder?: HTMLElement
  cancelToken?: CancelTokenSource

  constructor (props: HomepageEditorProps) {
    super(props)
    this.state = {
      isConfigured: true,
      configError: '',
      controlsVisible: false,
      loading: false
    }
    this.onDone = this.onDone.bind(this)
    this.onClose = this.onClose.bind(this)
    this.renderPreview = this.renderPreview.bind(this)
    this.onMouseOver = this.onMouseOver.bind(this)
    this.onMouseOut = this.onMouseOut.bind(this)
    this.onClose = this.onClose.bind(this)
  }

  onMouseOver () {
    if (this.props.disabled) return

    this.setState({
      controlsVisible: true
    })
    if (this.props.onMouseOver) this.props.onMouseOver()
  }

  onMouseOut () {
    if (this.props.disabled) return
    this.setState({
      controlsVisible: false
    })
    if (this.props.onMouseOut) this.props.onMouseOut()
  }

  componentDidUpdate (prevProps: HomepageEditorProps, prevState: State) {
    if (this.state.controlsVisible !== prevState.controlsVisible || this.props.homepageElement.active !== prevProps.homepageElement.active ||
      prevProps.edit !== this.props.edit || prevProps.disabled !== this.props.disabled) {
      this.updateParentElementClasses()
    }
  }

  updateParentElementClasses () {
    if (this.props.edit) {
      this.props.parentEl.classList.add('widget-container--edit')
    } else {
      this.props.parentEl.classList.remove('widget-container--edit')
    }

    if (this.state.controlsVisible && !this.props.disabled) {
      this.props.parentEl.classList.add('widget-container--hovered')
    } else {
      this.props.parentEl.classList.remove('widget-container--hovered')
    }

    if (this.props.homepageElement.active) {
      this.props.parentEl.classList.remove('widget-container--inactive')
    } else {
      this.props.parentEl.classList.add('widget-container--inactive')
    }
  }

  componentDidMount () {
    const placeholder = this.props.parentEl.querySelector('.widget-placeholder')
    if (isHtmlElement(placeholder)) {
      this.placeholder = placeholder
    }
    this.setConfigured()
    this.props.parentEl.addEventListener('mouseenter', this.onMouseOver)
    this.props.parentEl.addEventListener('mouseleave', this.onMouseOut)
    this.updateParentElementClasses()

    if (!placeholder) return

    if (placeholder.children.length) return

    this.renderPreview(this.props.homepageElement)
  }

  componentWillUnmount () {
    this.props.parentEl.removeEventListener('mouseenter', this.onMouseOver)
    this.props.parentEl.removeEventListener('mouseleave', this.onMouseOut)
  }

  setConfigured (): void {
    if (!this.placeholder) return this.renderEmptyPlaceholder()
    const children = this.placeholder.children
    if (!children.length && !this.placeholder.textContent?.trim()) return this.renderEmptyPlaceholder()

    switch (this.props.homepageElement.type) {
      case 'html_widget':
        return this.isHTMLConfigured(children)
      case 'knowledge_base':
        this.isKnowledgeBaseConfigured()
        return
      case 'feature_topics':
        return this.isLegacyFeatureTopicsConfigured(children)
      case 'recommendations':
        return this.isRecommendationsConfigured(children)
      case 'quick_links':
        return this.isQuickLinksConfigured(children)
      case 'featured_topics':
        return this.isFeaturedTopicsConfigured(children)
      case 'container_2_1':
        return this.setState({ isConfigured: true })
    }
  }

  renderEmptyPlaceholder () {
    this.setState({ isConfigured: false })
  }

  async isKnowledgeBaseConfigured () {
    const res = await getWithInMemoryCache('/site/knowledgeBase', {})
    if (!res.categoryGroups?.length) {
      this.renderEmptyPlaceholder()
    }
  }

  isLegacyFeatureTopicsConfigured (children: HTMLCollection) {
    if (!children[0].children.length) {
      this.renderEmptyPlaceholder()
    }
  }

  isHTMLConfigured (children: HTMLCollection) {
    if (!children[0].children.length && !children[0].textContent?.trim()) {
      this.renderEmptyPlaceholder()
    }
  }

  isQuickLinksConfigured (children: HTMLCollection) {
    const el = children[0]
    let data = []
    if (!isHtmlElement(el)) return
    try {
      const props = JSON.parse(el.dataset.props ?? '{}')
      data = props.data
    } catch (err) {}
    if (data.length) return
    this.renderEmptyPlaceholder()
  }

  isRecommendationsConfigured (children: HTMLCollection) {
    let data = []
    const el = children[0]
    if (!isHtmlElement(el)) return
    try {
      data = JSON.parse(el.dataset.props ?? '{}').curatedTopics
    } catch (err) {}
    if (data.length) return
    this.renderEmptyPlaceholder()
  }

  isFeaturedTopicsConfigured (children: HTMLCollection) {
    let data = []
    const el = children[0]

    if (!isHtmlElement(el)) return
    try {
      data = JSON.parse(el.dataset.props ?? '{}').topicIds
    } catch (err) {}
    if (data?.length) return
    this.renderEmptyPlaceholder()
  }

  moveNode (up: boolean): void {
    this.props.onMove(this.props.idx, { direction: up ? 'up' : 'down' })
  }

  toggleVisible () {
    const active = !this.props.homepageElement.active
    this.props.onVisibleToggle(this.props.idx, active)
  }

  toggleEdit () {
    this.props.onEdit(this.props.homepageElement)
  }

  remove () {
    this.props.onRemove(this.props.idx)
  }

  async renderPreview (element: ElementChange) {
    if (!this.placeholder) return
    this.cancelToken?.cancel()

    this.setState({
      isConfigured: true,
      configError: '',
      controlsVisible: false,
      loading: true
    })
    let res: AxiosResponse<string>

    try {
      this.cancelToken = Axios.CancelToken.source()
      res = await Axios.get('/widget/render', {
        params: {
          type: element.type,
          title: element.title,
          config: JSON.stringify(element.config)
        }
      })
    } catch (err) {
      if (Axios.isCancel(err)) return
      this.setState({ configError: err.response.data })
      return
    }
    this.placeholder.innerHTML = res.data
    this.setState({ loading: false, isConfigured: true })
    bindPreactComponents(this.placeholder)
    if (element.type === 'feature_topics') {
      window.app?.initDataElements(this.placeholder)
    }
    this.setConfigured()
  }

  onDone (element: ElementChange) {
    this.props.onDone({ element, idx: this.props.idx })
    this.setState({ controlsVisible: false })
    if (this.props.homepageElement.type === 'container_2_1') return
    this.renderPreview(element)
  }

  onClose () {
    this.setState({ controlsVisible: false })
    this.props.onClose()
    this.renderPreview(this.props.homepageElement)
  }

  render () {
    return (
      <div class={`widget-controls widget-controls--${this.props.homepageElement.type}`}>
        <div class='widget-tab-positioner' style={{ display: this.state.controlsVisible ? '' : 'none' }}>
          <div class='widget-tab'>
            <span style={{ marginRight: '10px' }}>
              {typeMap[this.props.homepageElement.type]}
              {sharedWidgetsList.includes(this.props.homepageElement.type) ? <Shared /> : null}
            </span>

            <TabButton icon={this.props.homepageElement.active ? 'eye' : 'eye-cross'} onClick={() => this.toggleVisible()} />

            {isEditableWidget(this.props.homepageElement) && <TabButton icon='edit' onClick={() => this.toggleEdit()} />}

            {isRemovableWidget(this.props.homepageElement, this.props.currentPage) && <TabButton icon='trash' onClick={() => this.remove()} />}

            {this.props.canMoveUp && <TabButton icon='arrow-up' onClick={() => this.moveNode(true)} />}

            {this.props.canMoveDown && <TabButton icon='arrow-down' onClick={() => this.moveNode(false)} />}

          </div>
        </div>

        {!this.props.homepageElement.active
          ? <div class='widget-non-active-state'>
            <Icon icon='eye-cross' color='currentColor' size={30} />
          </div>
          : null}

        {this.state.isConfigured
          ? null
          : <EmptyPlaceholder type={this.props.homepageElement.type} placeHolder={this.placeholder} />}

        {
        !this.props.homepageElement.nonConfigurable
          ? (
            <WidgetConfigurator
              isVisible={this.props.edit}
              controlUrl={this.props.controlUrl}
              onChange={this.renderPreview}
              onDone={this.onDone}
              error={this.state.configError}
              onClose={this.onClose}
              homepageElement={this.props.homepageElement}
              widgetTitle={typeMap[this.props.homepageElement.type]}
            />
            )
          : null
      }

        {this.props.homepageElement.type === 'container_2_1' && !this.state.loading &&
          <ContainerEditor
            onChange={this.onDone}
            controlUrl={this.props.controlUrl}
            homepageElement={this.props.homepageElement}
            onChildEnter={() => this.setState({ controlsVisible: false })}
            onChildLeave={() => this.setState({ controlsVisible: true })}
            currentPage={this.props.currentPage}
            parentEl={this.props.parentEl}
          />}
      </div>
    )
  }
}
